import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { AuditReportsTable } from '@/components/admin/audit-reports/audit-reports-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminAuditReports } from '@/hooks/data/admin/use-audit-reports';
import {
  AuditReportIntakeStatus,
  AuditReportReviewStatus,
} from '@/types/generated/enums';
import type {
  AuditReportIntakeStatus as AuditReportIntakeStatusType,
  AuditReportReviewStatus as AuditReportReviewStatusType,
} from '@/types/generated';

const searchSchema = z.object({
  review_status: z.nativeEnum(AuditReportReviewStatus).optional(),
  status: z.nativeEnum(AuditReportIntakeStatus).optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/audit-reports/')({
  validateSearch: searchSchema,
  staticData: { title: 'Audit report review queue' },
  component: AdminAuditReportsPage,
});

function AdminAuditReportsPage(): React.ReactElement {
  const { review_status, status, offset } = Route.useSearch();
  const navigate = Route.useNavigate();

  const auditReportsQuery = useAdminAuditReports({
    review_status,
    status,
    limit: LIMIT,
    offset,
  });

  const setReviewStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        review_status:
          value === 'ALL' ? undefined : (value as AuditReportReviewStatusType),
        offset: 0,
      }),
    });
  };

  const setIntakeStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        status:
          value === 'ALL' ? undefined : (value as AuditReportIntakeStatusType),
        offset: 0,
      }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Audit report review queue"
        description="Review, approve, and send assembled audit reports"
      />
      <FilterBar>
        <Select value={review_status ?? 'ALL'} onValueChange={setReviewStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Review status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All review statuses</SelectItem>
            <SelectItem value={AuditReportReviewStatus.PENDING}>
              Pending
            </SelectItem>
            <SelectItem value={AuditReportReviewStatus.APPROVED}>
              Approved
            </SelectItem>
            <SelectItem value={AuditReportReviewStatus.REJECTED}>
              Rejected
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={status ?? 'ALL'} onValueChange={setIntakeStatus}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Intake status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All intake statuses</SelectItem>
            <SelectItem value={AuditReportIntakeStatus.RECEIVED}>
              Received
            </SelectItem>
            <SelectItem value={AuditReportIntakeStatus.HALTED}>
              Halted
            </SelectItem>
            <SelectItem value={AuditReportIntakeStatus.READY}>Ready</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <AuditReportsTable
        query={auditReportsQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
      />
    </main>
  );
}
